import React, { useState, useEffect, useRef } from 'react';
import { Card, Typography, Image, Space, Row, Col, Avatar } from 'antd';
import { 
  LinkOutlined, 
  AppstoreOutlined, 
  FileOutlined, 
  PlayCircleOutlined,
  UserOutlined,
  ExpandOutlined
} from '@ant-design/icons';
import { useAuth } from '../../utils/auth';
import { processAvatarUrl } from '../../utils/urlUtils';
import './MessagePreview.css';

const { Text, Paragraph } = Typography;

/**
 * 获取OSS预览URL
 * @param {string} ossUrl OSS URL
 * @returns {Promise<string>} 可访问的预览URL
 */
const getPreviewUrl = async (ossUrl) => {
  if (!ossUrl) return null;
  
  // 如果已经是HTTP URL，直接返回
  if (ossUrl.startsWith('http')) {
    return ossUrl;
  }
  
  // 如果是OSS协议URL，请求转换
  if (ossUrl.startsWith('oss://')) {
    try {
      const response = await fetch(`/api/media/wework/oss/preview?url=${encodeURIComponent(ossUrl)}`);
      const data = await response.json();
      
      if (data.success && data.url) {
        return data.url;
      }
    } catch (error) {
      console.error('获取预览URL失败:', error);
    }
  }
  
  return null;
};

/**
 * 渲染文本消息
 */
const TextMessage = ({ content }) => {  
  // 根据内容长度动态设置样式
  const cardStyle = {
    display: 'inline-block',  // 使卡片宽度自适应内容
    maxWidth: '100%',         // 最大不超过容器宽度
    minWidth: '60px',         // 最小宽度
  };
  
  return (
    <Card 
      size="small" 
      className="message-card text-message" 
      bodyStyle={{ padding: '10px' }}
      style={cardStyle}
    >
      <Paragraph 
        style={{ 
          margin: 0, 
          wordBreak: 'break-word',
          whiteSpace: 'pre-wrap'  // 保留换行符和空格
        }}
      >
        {content}
      </Paragraph>
    </Card>
  );
};

/**
 * 渲染图片消息
 */
const ImageMessage = ({ ossUrl, mediaId }) => {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [containerWidth, setContainerWidth] = useState('auto');
  
  useEffect(() => {
    const fetchPreviewUrl = async () => {
      const url = await getPreviewUrl(ossUrl);
      setPreviewUrl(url);
      setLoading(false);
    };
    
    fetchPreviewUrl();
  }, [ossUrl]);

  // 图片加载完成后获取真实尺寸
  const handleImageLoad = (e) => {
    const img = e.target;
    const naturalWidth = img.naturalWidth;
    const naturalHeight = img.naturalHeight;
    
    // 设置最大宽度为300px，保持图片比例
    const maxWidth = 300;
    const maxHeight = 200;
    
    let displayWidth, displayHeight;
    
    if (naturalWidth <= maxWidth && naturalHeight <= maxHeight) {
      // 图片尺寸在限制范围内，使用原始尺寸
      displayWidth = naturalWidth;
      displayHeight = naturalHeight;
    } else {
      // 按比例缩放
      const widthRatio = maxWidth / naturalWidth;
      const heightRatio = maxHeight / naturalHeight;
      const ratio = Math.min(widthRatio, heightRatio);
      
      displayWidth = Math.floor(naturalWidth * ratio);
      displayHeight = Math.floor(naturalHeight * ratio);
    }
    
    setImageSize({ width: displayWidth, height: displayHeight });
    setContainerWidth(displayWidth);
  };
  
  return (
    <Card 
      size="small" 
      className="message-card image-message" 
      bodyStyle={{ padding: '4px' }}
      style={{ 
        width: containerWidth,
        maxWidth: '300px',
        display: 'inline-block'
      }}
    >
      {loading ? (
        <div style={{ width: '200px', height: '120px', display: 'flex', justifyContent: 'center', alignItems: 'center', background: '#f0f0f0' }}>
          加载中...
        </div>
      ) : previewUrl ? (
        <Image 
          src={previewUrl}
          alt="图片消息"
          style={{ 
            width: imageSize.width || 'auto',
            height: imageSize.height || 'auto',
            maxWidth: '300px',
            maxHeight: '200px',
            objectFit: 'contain'
          }}
          preview={{ src: previewUrl }}
          onLoad={handleImageLoad}
        />
      ) : (
        <div style={{ width: '200px', height: '120px', display: 'flex', justifyContent: 'center', alignItems: 'center', background: '#f0f0f0' }}>
          <Text type="secondary">图片预览</Text>
        </div>
      )}
    </Card>
  );
};

/**
 * 渲染视频消息
 */
const VideoMessage = ({ ossUrl, mediaId }) => {
  const [previewUrl, setPreviewUrl] = useState(null);
  
  useEffect(() => {
    const fetchPreviewUrl = async () => {
      const url = await getPreviewUrl(ossUrl);
      setPreviewUrl(url);
    };
    
    fetchPreviewUrl();
  }, [ossUrl]);
  
  return (
    <Card size="small" className="message-card video-message" bodyStyle={{ padding: '4px' }}>
      {previewUrl ? (
        <div style={{ 
          position: 'relative', 
          width: '100%', 
          backgroundColor: '#000',
          borderRadius: '4px',
          overflow: 'hidden',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100px'
        }}>
          <video
            src={previewUrl}
            style={{ 
              maxWidth: '100%', 
              maxHeight: '200px',
              width: 'auto',
              height: 'auto',
              objectFit: 'contain',
              display: 'block'
            }}
            controls={false}
          />
          <div style={{ 
            position: 'absolute', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            background: 'rgba(0,0,0,0.3)',
            pointerEvents: 'none'
          }}>
            <PlayCircleOutlined style={{ fontSize: '32px', color: '#fff' }} />
          </div>
        </div>
      ) : (
        <div style={{ 
          width: '100px', 
          height: '100px', 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          background: '#f0f0f0',
          borderRadius: '4px'
        }}>
          <Space direction="vertical" align="center" size="small">
            <PlayCircleOutlined style={{ fontSize: '28px', color: '#999' }} />
            <Text type="secondary" style={{ fontSize: '14px' }}>视频</Text>
          </Space>
        </div>
      )}
    </Card>
  );
};

/**
 * 渲染链接消息
 */
const LinkMessage = ({ title, url, description, picUrl, picUrl_ossUrl }) => {
  const [previewUrl, setPreviewUrl] = useState(null);
  
  useEffect(() => {
    const fetchPreviewUrl = async () => {
      // 优先使用picUrl_ossUrl，其次使用picUrl
      const imageUrl = picUrl_ossUrl || picUrl;
      if (imageUrl) {
        const url = await getPreviewUrl(imageUrl);
        setPreviewUrl(url);
      }
    };
    
    fetchPreviewUrl();
  }, [picUrl, picUrl_ossUrl]);
  
  return (
    <Card size="small" className="message-card link-message" bodyStyle={{ padding: '10px' }}>
      <div style={{ display: 'flex' }}>
        <div style={{ flex: 1 }}>
          <Text strong ellipsis>{title || '未设置标题'}</Text>
          <Paragraph type="secondary" ellipsis={{ rows: 2 }} style={{ fontSize: '12px', margin: '4px 0' }}>
            {description || url || '链接描述'}
          </Paragraph>
        </div>
        {previewUrl && (
          <div style={{ marginLeft: '8px', width: '40px', height: '40px' }}>
            <Image 
              src={previewUrl}
              alt="链接缩略图"
              style={{ width: '40px', height: '40px', objectFit: 'cover' }}
              preview={false}
            />
          </div>
        )}
        {!previewUrl && (
          <div style={{ marginLeft: '8px', width: '40px', height: '40px', background: '#f0f0f0', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <LinkOutlined style={{ color: '#bfbfbf' }} />
          </div>
        )}
      </div>
    </Card>
  );
};

/**
 * 渲染小程序消息
 */
const MiniProgramMessage = ({ title, appid, pagepath, thumbMediaId, thumbMediaId_ossUrl }) => {
  const [previewUrl, setPreviewUrl] = useState(null);
  const { user } = useAuth();
  const userName = user?.displayName || user?.username || '爱推';
  
  useEffect(() => {
    const fetchPreviewUrl = async () => {
      // 优先使用thumbMediaId_ossUrl
      const imageUrl = thumbMediaId_ossUrl;
      if (imageUrl) {
        const url = await getPreviewUrl(imageUrl);
        setPreviewUrl(url);
      }
    };
    
    fetchPreviewUrl();
  }, [thumbMediaId_ossUrl]);
  
  return (
    <Card 
      size="small" 
      className="message-card miniprogram-message" 
      bodyStyle={{ padding: '0' }}
      style={{ width: '200px', maxWidth: '200px' }}
    >
      {/* 小程序顶部标识 */}
      <div className="miniprogram-header">
        <Text type="secondary" style={{ fontSize: '12px' }}>{userName}</Text>
      </div>
      
      {/* 用户填写的标题 */}
      <div className="miniprogram-title">
        <Text 
          strong 
          ellipsis 
          style={{ 
            fontSize: '14px', 
            width: '100%',
            maxWidth: '176px'  // 200px - 12px左右padding
          }}
        >
          {title || '小程序'}
        </Text>
      </div>
      
      {/* 小程序封面图片 */}
      <div className="miniprogram-image" style={{ padding: '0px 12px' }}>
        {previewUrl ? (
          <Image 
            src={previewUrl}
            alt="小程序封面"
            style={{ width: '176px', height: '140px', objectFit: 'cover', borderRadius: '4px' }}
            preview={false}
          />
        ) : (
          <div style={{ width: '176px', height: '140px', background: '#f0f0f0', display: 'flex', justifyContent: 'center', alignItems: 'center', borderRadius: '4px' }}>
            <AppstoreOutlined style={{ fontSize: '24px', color: '#bfbfbf' }} />
          </div>
        )}
      </div>
      
      {/* 小程序底部标识 */}
      <div className="miniprogram-footer">
        <Text type="secondary" style={{ fontSize: '12px' }}>小程序</Text>
      </div>
    </Card>
  );
};

/**
 * 渲染文件消息
 */
const FileMessage = ({ mediaId, ossUrl }) => {
  return (
    <Card size="small" className="message-card file-message" bodyStyle={{ padding: '10px' }}>
      <Space align="center">
        <FileOutlined style={{ fontSize: '24px', color: '#1890ff' }} />
        <Text ellipsis style={{ maxWidth: '150px' }}>文件</Text>
      </Space>
    </Card>
  );
};

/**
 * 渲染朋友圈预览
 */
const MomentPreview = ({ text, mediaItems }) => {
  const [previewUrls, setPreviewUrls] = useState({});
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchPreviewUrls = async () => {
      const urls = {};
      
      // 处理不同类型的媒体项
      for (const item of mediaItems) {
        // 处理常规图片
        if ((item.type === 'MOMENT_IMAGE' || item.type === 'image' || item.type === 'IMAGE') && 
            item.params?.ossUrl && item.params?.mediaId) {
          const url = await getPreviewUrl(item.params.ossUrl);
          if (url) {
            urls[item.params.mediaId] = url;
          }
        }
        // 处理视频
        else if ((item.type === 'MOMENT_VIDEO' || item.type === 'video' || item.type === 'VIDEO') && 
                 item.params?.ossUrl && item.params?.mediaId) {
          console.log("MomentPreview 处理视频:", item);
          const url = await getPreviewUrl(item.params.ossUrl);
          console.log("MomentPreview 获取到视频URL:", url);
          if (url) {
            urls[item.params.mediaId] = url;
          }
        }
        // 处理图片组
        else if ((item.type === 'MOMENT_IMAGE_GROUP' || item.type === 'IMAGE_GROUP') && 
                 item.params?.imageGroup) {
          const imageGroup = item.params.imageGroup;
          
          // 为图片组中的每个图片获取预览URL
          if (imageGroup.mediaIds && imageGroup.ossUrls) {
            for (const mediaId of imageGroup.mediaIds) {
              const ossUrl = imageGroup.ossUrls[mediaId];
              if (ossUrl) {
                const url = await getPreviewUrl(ossUrl);
                if (url) {
                  urls[mediaId] = url;
                }
              }
            }
          }
        }
      }
      
      setPreviewUrls(urls);
      setLoading(false);
    };
    
    fetchPreviewUrls();
  }, [mediaItems]);
  
  // 提取所有非重复图片项
  const extractUniqueImageItems = () => {
    const uniqueItems = [];
    const processedMediaIds = new Set();
    const imageGroupItems = [];
    
    // 优先处理图片组
    mediaItems.forEach(item => {
      if ((item.type === 'MOMENT_IMAGE_GROUP' || item.type === 'IMAGE_GROUP') && 
          item.params?.imageGroup?.mediaIds?.length > 0) {
        imageGroupItems.push(item);
        // 记录图片组中包含的所有图片ID，避免重复
        item.params.imageGroup.mediaIds.forEach(id => {
          processedMediaIds.add(id);
        });
      }
    });
    
    // 接着处理非图片组项
    mediaItems.forEach(item => {
      // 视频和链接类型直接添加
      if (['MOMENT_VIDEO', 'MOMENT_LINK', 'VIDEO', 'LINK'].includes(item.type)) {
        uniqueItems.push(item);
      }
      // 对于图片，检查是否已经在图片组中
      else if ((item.type === 'MOMENT_IMAGE' || item.type === 'IMAGE') && item.params?.mediaId) {
        // 如果该图片没有在任何图片组中处理过，则添加
        if (!processedMediaIds.has(item.params.mediaId)) {
          uniqueItems.push(item);
          processedMediaIds.add(item.params.mediaId);
        }
      }
    });
    
    // 如果有图片组，优先使用图片组中的所有图片
    if (imageGroupItems.length > 0) {
      imageGroupItems.forEach(group => {
        const mediaIds = group.params.imageGroup.mediaIds;
        const ossUrls = group.params.imageGroup.ossUrls;
        
        // 为图片组中的每张图片创建单独的图片项
        mediaIds.forEach(mediaId => {
          uniqueItems.push({
            type: 'MOMENT_IMAGE',
            params: {
              mediaId: mediaId,
              ossUrl: ossUrls[mediaId] || ''
            }
          });
        });
      });
    }
    
    return uniqueItems;
  };
  
  // 获取所有非重复图片
  const uniqueItems = extractUniqueImageItems();
  
  // 从唯一项中筛选图片
  const allImages = uniqueItems.filter(item => 
    item.type === 'MOMENT_IMAGE' || item.type === 'IMAGE'
  );
  
  // 计算图片布局方式
  const getGridConfig = (count) => {
    if (count === 1) return { span: 24, cols: 1 };
    if (count === 4) return { span: 12, cols: 2 }; // 2x2布局
    return { span: 8, cols: 3 }; // 默认3列布局
  };
  
  const imageCount = allImages.length;
  const gridConfig = getGridConfig(imageCount);
  
  return (
    <div className="message-wrapper">
      <MessageAvatar />
      <div className="message-content">
        <Card size="small" className="moment-preview" bodyStyle={{ padding: '12px' }}>
          {/* 朋友圈文字内容 */}
          {text && (
            <Paragraph 
              style={{ 
                margin: 0, 
                marginBottom: '8px', 
                wordBreak: 'break-word',
                whiteSpace: 'pre-wrap'  // 保留换行符和空格
              }}
            >
              {text}
            </Paragraph>
          )}
          
          {/* 朋友圈媒体内容 */}
          {loading ? (
            <div style={{ height: '60px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              加载中...
            </div>
          ) : (
            <Row gutter={[8, 8]}>
              {/* 图片项 */}
              {allImages.map((item, index) => {
                // 获取媒体ID和预览URL
                const mediaId = item.params?.mediaId;
                const previewUrl = previewUrls[mediaId];
                
                return (
                  <Col key={index} span={gridConfig.span}>
                    {previewUrl ? (
                      <Image 
                        src={previewUrl}
                        alt={`朋友圈图片${index+1}`}
                        style={{ 
                          width: '100%', 
                          height: gridConfig.cols === 1 ? '200px' : '80px', 
                          objectFit: 'cover'
                        }}
                        preview={{ src: previewUrl }}
                      />
                    ) : (
                      <div style={{ 
                        width: '100%', 
                        height: gridConfig.cols === 1 ? '200px' : '80px', 
                        background: '#f0f0f0', 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center' 
                      }}>
                        <Text type="secondary">图片</Text>
                      </div>
                    )}
                  </Col>
                );
              })}
              
              {/* 处理视频 - 视频独占更大空间 */}
              {uniqueItems.filter(item => 
                item.type === 'MOMENT_VIDEO' || item.type === 'VIDEO'
              ).map((item, index) => {
                const mediaId = item.params?.mediaId;
                const previewUrl = previewUrls[mediaId];
                
                // 视频使用更大的span，给竖屏视频更多空间
                const videoSpan = gridConfig.cols === 1 ? 24 : (gridConfig.cols === 2 ? 12 : 16); // 单个占全宽，2列时占一半，3列时占2/3
                
                return (
                  <Col key={`video-${index}`} span={videoSpan}>
                    {previewUrl ? (
                      <div 
                        className="moment-video-item"
                        style={{ 
                          position: 'relative', 
                          width: '100%',
                          backgroundColor: '#000',
                          borderRadius: '4px',
                          overflow: 'hidden',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          minHeight: '120px',
                          maxHeight: '250px'
                        }}
                      >
                        <video
                          src={previewUrl}
                          style={{ 
                            maxWidth: '100%', 
                            maxHeight: '250px',
                            width: 'auto',
                            height: 'auto',
                            objectFit: 'contain',
                            display: 'block'
                          }}
                          controls={false}
                        />
                        <div style={{ 
                          position: 'absolute', 
                          top: 0, 
                          left: 0, 
                          right: 0, 
                          bottom: 0, 
                          display: 'flex', 
                          justifyContent: 'center', 
                          alignItems: 'center',
                          background: 'rgba(0,0,0,0.3)',
                          pointerEvents: 'none'
                        }}>
                          <PlayCircleOutlined style={{ fontSize: '28px', color: '#fff' }} />
                        </div>
                      </div>
                    ) : (
                      <div 
                        className="moment-video-item"
                        style={{ 
                          width: '100px', 
                          height: '100px',
                          background: '#f0f0f0', 
                          display: 'flex', 
                          justifyContent: 'center', 
                          alignItems: 'center',
                          borderRadius: '4px'
                        }}
                      >
                        <Space direction="vertical" align="center" size="small">
                          <PlayCircleOutlined style={{ fontSize: '28px', color: '#999' }} />
                          <Text type="secondary" style={{ fontSize: '14px' }}>视频</Text>
                        </Space>
                      </div>
                    )}
                  </Col>
                );
              })}
            </Row>
          )}
        </Card>
      </div>
    </div>
  );
};

/**
 * 消息头像组件
 */
const MessageAvatar = () => {
  const { user } = useAuth();
  const avatarUrl = processAvatarUrl(user?.avatar);
  const [imageError, setImageError] = useState(false);
  
  return (
    <div className="message-avatar" style={{ flexShrink: 0 }}>
      {avatarUrl && !imageError ? (
        <img 
          src={avatarUrl}
          alt="用户头像"
          style={{
            width: '34px',
            height: '34px',
            objectFit: 'cover',
            borderRadius: '4px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.12)'
          }}
          onError={() => setImageError(true)}
        />
      ) : (
        <div
          style={{
            width: '34px',
            height: '34px',
            backgroundColor: '#f0f0f0',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            borderRadius: '4px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.12)'
          }}
        >
          <UserOutlined style={{ fontSize: '18px', color: '#999' }} />
        </div>
      )}
    </div>
  );
};

/**
 * 消息预览组件
 */
const MessagePreview = ({ step, previewUrls = {}, noTruncate = false }) => {
  const [loading, setLoading] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const contentRef = useRef(null);
  
  // 检测内容是否超过高度限制
  useEffect(() => {
    if (contentRef.current && !noTruncate) {
      const element = contentRef.current;
      const isOverflow = element.scrollHeight > 120; // 120px是我们设置的截断高度
      setIsOverflowing(isOverflow);
    }
  }, [step, noTruncate]);
  
  // 根据步骤类型和内容渲染不同的预览
  const renderPreview = () => {
    // 针对朋友圈类型
    if (step.type === 'MOMENTS' || step.actionType === 'MOMENTS' || step.actionType === 'SEND_MOMENT') {
      // 获取朋友圈文本内容
      let text = '';
      let mediaItems = [];
      
      // 从momentsList获取内容
      if (step.momentsList && step.momentsList.length > 0) {
        // 提取文本内容
        const textItem = step.momentsList.find(item => 
          item.type === 'MOMENT_TEXT' || item.type === 'text' || item.type === 'TEXT'
        );
        if (textItem) {
          text = textItem.params?.content || '';
        }
        
        // 提取媒体内容 - 包括单独图片、图片组和视频
        mediaItems = step.momentsList.filter(item => 
          item.type === 'MOMENT_IMAGE' || 
          item.type === 'MOMENT_VIDEO' || 
          item.type === 'MOMENT_IMAGE_GROUP' ||
          item.type === 'image' || item.type === 'video' ||
          item.type === 'IMAGE' || item.type === 'VIDEO' ||
          item.type === 'IMAGE_GROUP'
        );
      } 
      // 从actionParams获取内容
      else if (step.actionParams) {
        let params = step.actionParams;
        if (typeof params === 'string') {
          try {
            params = JSON.parse(params);
          } catch (e) {
            console.error('解析actionParams失败:', e);
            params = {};
          }
        }
        
        // 提取文本内容
        if (params.text && params.text.content) {
          text = params.text.content;
        }
        
        // 提取媒体内容
        if (params.attachments && Array.isArray(params.attachments)) {
          // 收集图片和视频附件
          const imageAttachments = [];
          const videoItems = [];
          
          params.attachments.forEach(attachment => {
            if (attachment.msgtype === 'image') {
              imageAttachments.push({
                type: 'MOMENT_IMAGE',
                params: {
                  mediaId: attachment.image.media_id,
                  ossUrl: attachment.image.oss_url || ''
                }
              });
            } else if (attachment.msgtype === 'video') {
              videoItems.push({
                type: 'MOMENT_VIDEO',
                params: {
                  mediaId: attachment.video.media_id,
                  ossUrl: attachment.video.oss_url || ''
                }
              });
            }
          });
          
          // 如果有多张图片，创建一个IMAGE_GROUP
          if (imageAttachments.length > 1) {
            const groupId = 'moment-image-group-' + Date.now();
            const imageGroup = {
              type: 'MOMENT_IMAGE_GROUP',
              params: {
                imageGroup: {
                  mediaIds: [],
                  ossUrls: {}
                }
              }
            };
            
            // 添加所有图片到图片组
            imageAttachments.forEach(img => {
              const mediaId = img.params.mediaId;
              imageGroup.params.imageGroup.mediaIds.push(mediaId);
              imageGroup.params.imageGroup.ossUrls[mediaId] = img.params.ossUrl || '';
            });
            
            // 合并媒体项
            mediaItems = [imageGroup, ...imageAttachments, ...videoItems];
          } else {
            // 直接使用单独的图片和视频
            mediaItems = [...imageAttachments, ...videoItems];
          }
        }
      }
      
      return (
        <div className="phone-simulator" style={{
          backgroundColor: '#ededed',
          borderRadius: '20px',
          overflow: 'hidden',
          maxWidth: '320px',
          margin: '0 auto',
        }}>
          {/* 手机顶部状态栏 */}
          <div className="phone-header" style={{
            position: 'relative',
            width: '100%',
            height: 'auto'
          }}>
            <img 
              src="/header.png" 
              alt="手机状态栏"
              style={{
                width: '100%',
                height: 'auto',
                display: 'block'
              }}
            />
          </div>
          
          {/* 朋友圈内容区域 */}
          <div className="moments-content" style={{
            height: '500px',
            overflowY: 'auto',
            padding: '12px',
            backgroundColor: '#ededed',
            scrollbarWidth: 'thin',
            scrollbarColor: '#bbb transparent'
          }}>
            <MomentPreview text={text} mediaItems={mediaItems} />
          </div>
        </div>
      );
    }
    
    // 针对消息类型
    if (step.type === 'MESSAGE' || 
        step.actionType === 'SEND_MESSAGE' || 
        ['TEXT', 'IMAGE', 'VIDEO', 'LINK', 'MINIPROGRAM', 'FILE'].includes(step.actionType)) {
      
      // 从messageList获取内容
      if (step.messageList && step.messageList.length > 0) {
        // 提取所有非图片组项和非重复图片
        const uniqueItems = [];
        const processedMediaIds = new Set();
        const imageGroupItems = [];
        
        // 优先处理图片组
        step.messageList.forEach(item => {
          if (item.type === 'IMAGE_GROUP' && item.params?.imageGroup?.mediaIds?.length > 0) {
            imageGroupItems.push(item);
            // 记录图片组中包含的所有图片ID，避免重复
            item.params.imageGroup.mediaIds.forEach(id => {
              processedMediaIds.add(id);
            });
          }
        });
        
        // 接着处理非图片组项
        step.messageList.forEach(item => {
          // 文本、链接、小程序、文件类型直接添加
          if (['TEXT', 'LINK', 'MINIPROGRAM', 'FILE', 'VIDEO'].includes(item.type)) {
            uniqueItems.push(item);
          }
          // 对于图片，检查是否已经在图片组中
          else if (item.type === 'IMAGE' && item.params?.mediaId) {
            // 如果该图片没有在任何图片组中处理过，则添加
            if (!processedMediaIds.has(item.params.mediaId)) {
              uniqueItems.push(item);
              processedMediaIds.add(item.params.mediaId);
            }
          }
        });
        
        // 如果有图片组，优先使用图片组中的所有图片
        if (imageGroupItems.length > 0) {
          imageGroupItems.forEach(group => {
            const mediaIds = group.params.imageGroup.mediaIds;
            const ossUrls = group.params.imageGroup.ossUrls;
            
            // 为图片组中的每张图片创建单独的图片项
            mediaIds.forEach(mediaId => {
              uniqueItems.push({
                type: 'IMAGE',
                params: {
                  mediaId: mediaId,
                  ossUrl: ossUrls[mediaId] || ''
                }
              });
            });
          });
          
          // 移除处理过的图片组，避免重复处理
          imageGroupItems.length = 0;
        }
        
        // 按照顺序渲染所有消息
        return (
          <div className="phone-simulator" style={{
            backgroundColor: '#ededed',
            borderRadius: '20px',
            overflow: 'hidden',
            maxWidth: '320px',
            margin: '0 auto',
          }}>
            {/* 手机顶部状态栏 */}
            <div className="phone-header" style={{
              position: 'relative',
              width: '100%',
              height: 'auto'
            }}>
              <img 
                src="/header.png" 
                alt="手机状态栏"
                style={{
                  width: '100%',
                  height: 'auto',
                  display: 'block'
                }}
              />
            </div>
            
            {/* 消息内容区域 */}
            <div className="messages-content" style={{
              height: '500px',
              overflowY: 'auto',
              padding: '12px',
              backgroundColor: '#ededed',
              scrollbarWidth: 'thin',
              scrollbarColor: '#bbb transparent'
            }}>
              <div className="messages-group">
                {uniqueItems.map((message, index) => {
                  switch (message.type) {
                    case 'TEXT':
                      return (
                        <div key={index} className="message-wrapper" style={{ width: '100%', display: 'flex', marginBottom: '10px'}}>
                          <MessageAvatar />
                          <TextMessage content={message.params?.content || ''} />
                        </div>
                      );
                    case 'IMAGE':
                      return (
                        <div key={index} className="message-wrapper" style={{ marginBottom: '12px' }}>
                          <MessageAvatar />
                          <div className="message-content">
                            <ImageMessage 
                              ossUrl={message.params?.ossUrl || ''}
                              mediaId={message.params?.mediaId || ''}
                            />
                          </div>
                        </div>
                      );
                    case 'VIDEO':
                      return (
                        <div key={index} className="message-wrapper" style={{ marginBottom: '12px' }}>
                          <MessageAvatar />
                          <div className="message-content">
                            <VideoMessage 
                              ossUrl={message.params?.ossUrl || ''}
                              mediaId={message.params?.mediaId || ''}
                            />
                          </div>
                        </div>
                      );
                    case 'LINK':
                      return (
                        <div key={index} className="message-wrapper" style={{ marginBottom: '12px' }}>
                          <MessageAvatar />
                          <div className="message-content">
                            <LinkMessage
                              title={message.params?.title || ''}
                              url={message.params?.url || ''}
                              description={message.params?.description || ''}
                              picUrl={message.params?.picUrl || ''}
                              picUrl_ossUrl={message.params?.picUrl_ossUrl || ''}
                            />
                          </div>
                        </div>
                      );
                case 'MINIPROGRAM':
                  return (
                    <div key={index} className="message-wrapper" style={{ marginBottom: '12px' }}>
                      <MessageAvatar />
                      <div className="message-content">
                        <MiniProgramMessage
                          title={message.params?.title || ''}
                          appid={message.params?.appid || ''}
                          pagepath={message.params?.pagepath || ''}
                          thumbMediaId={message.params?.thumbMediaId || ''}
                          thumbMediaId_ossUrl={message.params?.thumbMediaId_ossUrl || ''}
                        />
                      </div>
                    </div>
                  );
                case 'FILE':
                  return (
                    <div key={index} className="message-wrapper" style={{ marginBottom: '12px' }}>
                      <MessageAvatar />
                      <div className="message-content">
                        <FileMessage
                          mediaId={message.params?.mediaId || ''}
                          ossUrl={message.params?.ossUrl || ''}
                        />
                      </div>
                    </div>
                  );
                default:
                  return <Text key={index} type="secondary">未知消息类型: {message.type}</Text>;
              }
            })}
              </div>
            </div>
          </div>
        );
      } 
      // 从actionParams获取内容
      else if (step.actionParams) {
        let params = step.actionParams;
        if (typeof params === 'string') {
          try {
            params = JSON.parse(params);
          } catch (e) {
            // 如果是纯文本，则直接使用
            if (typeof params === 'string') {
              return (
                <div className="message-wrapper" style={{ width: '100%', display: 'flex' }}>
                  <MessageAvatar />
                  <TextMessage content={params} />
                </div>
              );
            }
            console.error('解析actionParams失败:', e);
            params = {};
          }
        }
        
        // 单一文本消息
        if (step.actionType === 'TEXT' || params.content) {
          return (
            <div className="message-wrapper" style={{ width: '100%', display: 'flex' }}>
              <MessageAvatar />
              <TextMessage content={params.content || ''} />
            </div>
          );
        }
        
        // 从attachments解析媒体消息，显示所有附件而不仅是第一个
        if (params.attachments && Array.isArray(params.attachments) && params.attachments.length > 0) {
          return (
            <div className="messages-group">
              {params.attachments.map((attachment, index) => {
                if (attachment.msgtype === 'text') {
                  return (
                    <div key={index} className="message-wrapper" style={{ width: '100%', display: 'flex' }}>
                      <MessageAvatar />
                      <TextMessage content={attachment.text?.content || ''} />
                    </div>
                  );
                } else if (attachment.msgtype === 'image') {
                  return (
                    <div key={index} className="message-wrapper">
                      <MessageAvatar />
                      <div className="message-content">
                        <ImageMessage 
                          ossUrl={attachment.image.oss_url || ''}
                          mediaId={attachment.image.media_id || attachment.image.pic_url || ''}
                        />
                      </div>
                    </div>
                  );
                } else if (attachment.msgtype === 'video') {
                  return (
                    <div key={index} className="message-wrapper">
                      <MessageAvatar />
                      <div className="message-content">
                        <VideoMessage 
                          ossUrl={attachment.video.oss_url || ''}
                          mediaId={attachment.video.media_id || ''}
                        />
                      </div>
                    </div>
                  );
                } else if (attachment.msgtype === 'link') {
                  return (
                    <div key={index} className="message-wrapper">
                      <MessageAvatar />
                      <div className="message-content">
                        <LinkMessage
                          title={attachment.link.title || ''}
                          url={attachment.link.url || ''}
                          description={attachment.link.desc || ''}
                          picUrl={attachment.link.picurl || ''}
                          picUrl_ossUrl={attachment.link.pic_oss_url || ''}
                        />
                      </div>
                    </div>
                  );
                } else if (attachment.msgtype === 'miniprogram') {
                  return (
                    <div key={index} className="message-wrapper">
                      <MessageAvatar />
                      <div className="message-content">
                        <MiniProgramMessage
                          title={attachment.miniprogram.title || ''}
                          appid={attachment.miniprogram.appid || ''}
                          pagepath={attachment.miniprogram.page || ''}
                          thumbMediaId={attachment.miniprogram.pic_media_id || ''}
                          thumbMediaId_ossUrl={attachment.miniprogram.pic_oss_url || ''}
                        />
                      </div>
                    </div>
                  );
                } else if (attachment.msgtype === 'file') {
                  return (
                    <div key={index} className="message-wrapper">
                      <MessageAvatar />
                      <div className="message-content">
                        <FileMessage
                          mediaId={attachment.file.media_id || ''}
                          ossUrl={attachment.file.oss_url || ''}
                        />
                      </div>
                    </div>
                  );
                }
                return null;
              })}
            </div>
          );
        }
      }
      
      // 兜底处理特定类型
      if (step.actionType === 'IMAGE') {
        return (
          <div className="message-wrapper">
            <MessageAvatar />
            <div className="message-content">
              <ImageMessage 
                ossUrl={step.mediaId_ossUrl || ''}
                mediaId={step.mediaId || ''}
              />
            </div>
          </div>
        );
      } else if (step.actionType === 'VIDEO') {
        return (
          <div className="message-wrapper">
            <MessageAvatar />
            <div className="message-content">
              <VideoMessage 
                ossUrl={step.mediaId_ossUrl || ''}
                mediaId={step.mediaId || ''}
              />
            </div>
          </div>
        );
      } else if (step.actionType === 'LINK') {
        return (
          <div className="message-wrapper">
            <MessageAvatar />
            <div className="message-content">
              <LinkMessage
                title={step.title || ''}
                url={step.url || ''}
                description={step.description || ''}
                picUrl={step.picUrl || ''}
                picUrl_ossUrl={step.picUrl_ossUrl || ''}
              />
            </div>
          </div>
        );
      } else if (step.actionType === 'MINIPROGRAM') {
        return (
          <div className="message-wrapper">
            <MessageAvatar />
            <div className="message-content">
              <MiniProgramMessage
                title={step.title || ''}
                appid={step.appid || ''}
                pagepath={step.pagepath || ''}
                thumbMediaId={step.thumbMediaId || ''}
                thumbMediaId_ossUrl={step.thumbMediaId_ossUrl || ''}
              />
            </div>
          </div>
        );
      }
    }
    
    // 兜底显示
    return <Text type="secondary">暂无预览</Text>;
  };
  
  // 根据组件类型添加不同的样式
  let containerStyle = { 
    marginTop: '0px', 
    width: '100%',
    maxWidth: '360px',
    margin: '0 auto'
  };
  
  return (
    <div 
      className="message-preview-container" 
      style={containerStyle}
      onMouseEnter={() => !noTruncate && setIsHovered(true)}
      onMouseLeave={() => !noTruncate && setIsHovered(false)}
    >
      <div 
        ref={contentRef}
        className={`message-preview-content ${
          !noTruncate && isOverflowing && !isHovered ? 'message-preview-truncated' : 'message-preview-expanded'
        }`}
      >
        {renderPreview()}
      </div>
      
      {/* 展开提示 */}
      {!noTruncate && isOverflowing && !isHovered && (
        <div className="message-preview-expand-hint">
          <ExpandOutlined style={{ marginRight: 4 }} />
          hover展开
        </div>
      )}
    </div>
  );
};

export default MessagePreview;